<script setup lang="ts">
/**
 * Composer at the bottom of the chat pane.
 *
 * Keyboard contract:
 *   - Enter         — newline (don't accidentally send long prompts).
 *   - Cmd+Enter     — send (macOS).
 *   - Ctrl+Enter    — send (Windows/Linux; covers cross-platform users).
 *
 * The textarea is uncontrolled-ish: we own a local `text` ref and emit
 * `send` on submit. The parent clears the field by calling `clear()`
 * exposed via `defineExpose` — keeps the parent from having to wire a
 * v-model just to reset.
 */
import { ref } from 'vue';

const props = defineProps<{
  disabled: boolean;
  /** Streaming in progress — show stop affordance instead of send. */
  streaming: boolean;
  placeholder?: string;
}>();

const emit = defineEmits<{
  (e: 'send', text: string): void;
  (e: 'stop'): void;
}>();

const text = ref('');
const textarea = ref<HTMLTextAreaElement | null>(null);

function submit(): void {
  const trimmed = text.value.trim();
  if (!trimmed || props.disabled) return;
  emit('send', trimmed);
  text.value = '';
}

function onKeydown(e: KeyboardEvent): void {
  // Cmd+Enter (macOS) or Ctrl+Enter (other) sends.
  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
    e.preventDefault();
    submit();
  }
}

function focus(): void {
  textarea.value?.focus();
}

defineExpose({ focus });
</script>

<template>
  <div class="border-t border-border bg-panel px-6 py-4">
    <div class="max-w-3xl mx-auto flex flex-col gap-2">
      <textarea
        ref="textarea"
        v-model="text"
        rows="3"
        :placeholder="props.placeholder ?? 'Type a message. Cmd+Enter to send.'"
        :disabled="props.disabled && !props.streaming"
        class="input resize-none font-sans text-sm leading-relaxed"
        @keydown="onKeydown"
      />
      <div class="flex items-center justify-between text-xs text-muted">
        <span>Cmd+Enter (Ctrl+Enter) to send</span>
        <div class="flex gap-2">
          <button
            v-if="props.streaming"
            type="button"
            class="btn-ghost"
            @click="emit('stop')"
          >
            Stop
          </button>
          <button
            type="button"
            class="btn-primary"
            :disabled="props.disabled || text.trim().length === 0"
            @click="submit"
          >
            Send
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
